import type { ElectronClient, StorageWipeMarker } from '@openphone/desktop-client'
import { ForbiddenError, UpgradeRequiredError } from '@openphone/internal-api-client'
import { assertNever } from '@openphone/lib/assertNever'
import { formatDateToISO } from '@openphone/lib/date'
import { isPwa } from '@openphone/lib/device'
import { fromQueryString } from '@openphone/lib/fromQueryString'
import isNonNull from '@openphone/lib/isNonNull'
import type { LogoutReason } from '@openphone/quo-observability'
import { setUser, trackCustomAction } from '@openphone/quo-observability'
import type { Env } from '@openphone/web-config'
import Debug from 'debug'
import { action, flowResult, makeAutoObservable, reaction, runInAction, when } from 'mobx'
import type { Location, createBrowserRouter } from 'react-router-dom'
import { NavigationType } from 'react-router-dom'
import type { Observable } from 'rxjs'
import { asyncScheduler, of } from 'rxjs'
import { filter, mergeMap, throttleTime } from 'rxjs/operators'

import { MAX_VISIBLE_RECENTLY_VIEWED_HISTORY_ITEMS } from '@src/app/components/history-navigation/RecentlyViewedHistory/RecentlyViewedHistory'
import isSameLocation from '@src/app/router/isSameLocation'
import type { EmojiPickerPopoverProps } from '@src/component/EmojiPickerPopover/EmojiPickerPopover'
import type { MediaViewerState } from '@src/component/MediaViewer'
import config from '@src/config'
import type LoadableStatus from '@src/lib/LoadableStatus'
import delayObservable from '@src/lib/delayObservable/delayObservable'
import exponentialDelay from '@src/lib/exponentialDelay'
import { logError } from '@src/lib/log'
import permissions from '@src/permissions'
import type Service from '@src/service'
import type { IdentifyData } from '@src/service/analytics'
import { setKetchIdentities } from '@src/service/ketch/ketch'
import type { MessageMediaModel } from '@src/service/model'
import type StorageService from '@src/service/storage/StorageService'
import makePersistable from '@src/service/storage/makePersistable'
import type { TypographyThemeKey } from '@ui/theme'
import type { ThemeVariant } from '@ui/theme/ThemeVariant'
import getSafeThemeKey from '@ui/theme/getSafeThemeKey'
import getSafeThemeVariant from '@ui/theme/getSafeThemeVariant'
import type { StorageThemeKey } from '@ui/theme/isStorageThemeKey'

import NotificationController from './NotificationController'
import CommandUiStore from './command/CommandUiStore'
import { ErrorHelper } from './error'
import IntentStore from './intent/IntentStore'
import OnboardingExperienceStore from './onboarding/OnboardingExperienceStore'
import PromptUiStore from './prompt'
import type {
  LocationSearch,
  LocationSearchParam,
  SafeAbsoluteNavigateFunction,
} from './router'
import {
  createSafeAbsoluteNavigate,
  stripSearchParam,
  stripUnknownParams,
} from './router'
import Sift from './sift'
import { ToastUiStore } from './toast/ToastUiStore'
import AppUpdateController from './update/AppUpdateController'
import { URLController } from './url'
import { VoiceUiStore } from './voice'
import CallPopOutController from './voice/store/CallPopOutController'
import UserMediaDeviceAccessUiStore from './voice/user-media-device-store/UserMediaDeviceUIStore'
import UMDAElectronStrategy from './voice/user-media-device-store/strategies/UMDAElectronStrategy'
import UMDAWebStrategy from './voice/user-media-device-store/strategies/UMDAWebStrategy'

import { OdysseyAnalyticsBridge } from '@odyssey/analytics/OdysseyAnalyticsBridge'
import type { GuidesAndSurveysThemeMode } from '@odyssey/analytics/providers/GuidesAndSurveysProvider'
import { isExperimentFromReferral } from '@odyssey/feature-flags/experimentFromReferral'
import { getRootLogger, setRootLogger } from '@odyssey/logging/logger'

type Router = ReturnType<typeof createBrowserRouter>

/**
 * Maps app theme key to Amplitude Guides & Surveys theme mode
 */
function toAmplitudeThemeMode(themeKey: StorageThemeKey): GuidesAndSurveysThemeMode {
  switch (themeKey) {
    case 'system':
      return 'auto'
    case 'dark':
      return 'darkMode'
    case 'light':
      return 'lightMode'
  }
}

type EmojiPickerState = Omit<EmojiPickerPopoverProps, 'targetRef'> &
  (
    | {
        open: true
        targetRef: EmojiPickerPopoverProps['targetRef']
      }
    | {
        open: false
        targetRef: null
      }
  )

const HAS_BEEN_SET_AS_DEFAULT_TEL_PROTOCOL = 'HAS_BEEN_SET_AS_DEFAULT_TEL_PROTOCOL'

async function loadUiStores() {
  const [
    AlertsUiStore,
    AnalyticsUiStore,
    BillingUiStore,
    CallActivityUiStore,
    CnamUiStore,
    ContactsUiStore,
    ConversationUiStore,
    HelpAndSupportUiStore,
    InboxesUiStore,
    PortRequestUiStore,
    ScheduledMessagesUiStore,
    SideMenuUiStore,
    PhoneNumberUiStore,
    TollFreeRegistrationUiStore,
    TrustRegistrationUiStore,
    WorkspaceUiStore,
    CallsViewUiStore,
    AiCallAssistantUiStore,
    AiKnowledgeUiStore,
    AiCustomJobsV1UiStore,
    AiInboxesUiStore,
  ] = await Promise.all([
    import('./alerts/AlertsUiStore').then((m) => m.AlertsUiStore),
    import('./analytics/AnalyticsUiStore').then((m) => m.default),
    import('./billing/BillingUiStore').then((m) => m.default),
    import('./inbox/conversation/activity/CallActivityUiStore').then((m) => m.default),
    import('./cnam').then((m) => m.CnamUiStore),
    import('./contacts/ContactsUiStore').then((m) => m.default),
    import('./inbox/conversation/ConversationUiStore').then((m) => m.default),
    import('@src/app/help-and-support/HelpAndSupportUiStore').then((m) => m.default),
    import('./inbox/InboxesUiStore').then((m) => m.default),
    import('./porting').then((m) => m.PortRequestUiStore),
    import('./scheduled-message/ScheduledMessagesUiStore').then((m) => m.default),
    import('./side-menu/SideMenuUiStore').then((m) => m.default),
    import('./settings/phone-number/PhoneNumberUiStore').then((m) => m.default),
    import('./toll-free-registration').then((m) => m.TollFreeRegistrationUiStore),
    import('./trust-registration').then((m) => m.TrustRegistrationUiStore),
    import('./workspace').then((m) => m.WorkspaceUiStore),
    import('./inbox/calls/store/CallsViewUiStore').then((m) => m.default),
    import('./settings/phone-number/AiCallAssistant/AiCallAssistantUiStore').then(
      (m) => m.default,
    ),
    import('./Automations/Knowledge/AiKnowledgeUiStore').then((m) => m.default),
    import('./Automations/CustomJobsV1/AiCustomJobsV1UiStore').then((m) => m.default),
    import('./Automations/Inboxes/AiInboxesUiStore').then((m) => m.default),
  ])

  return {
    AlertsUiStore,
    AnalyticsUiStore,
    BillingUiStore,
    CallActivityUiStore,
    CnamUiStore,
    ContactsUiStore,
    ConversationUiStore,
    HelpAndSupportUiStore,
    InboxesUiStore,
    PortRequestUiStore,
    ScheduledMessagesUiStore,
    SideMenuUiStore,
    PhoneNumberUiStore,
    TollFreeRegistrationUiStore,
    TrustRegistrationUiStore,
    WorkspaceUiStore,
    CallsViewUiStore,
    AiCallAssistantUiStore,
    AiKnowledgeUiStore,
    AiCustomJobsV1UiStore,
    AiInboxesUiStore,
  } as const
}

type LoadedTypes = Awaited<ReturnType<typeof loadUiStores>>

type UiStoreTypes = {
  [K in keyof LoadedTypes]: InstanceType<LoadedTypes[K]>
}

export default class AppStore {
  readonly command: CommandUiStore
  readonly history: HistoryManager
  readonly onboarding: OnboardingExperienceStore

  readonly debug = {
    enable(value?: string) {
      Debug.enable(typeof value === 'string' ? value : '*')
    },
    disable() {
      Debug.disable()
    },
  }

  private get log() {
    return getRootLogger().getChild(['AppStore'])
  }

  private _alerts: UiStoreTypes['AlertsUiStore'] | null = null
  private _billing: UiStoreTypes['BillingUiStore'] | null = null
  private _callActivity: UiStoreTypes['CallActivityUiStore'] | null = null
  private _contacts: UiStoreTypes['ContactsUiStore'] | null = null
  private _conversation: UiStoreTypes['ConversationUiStore'] | null = null
  private _analytics: UiStoreTypes['AnalyticsUiStore'] | null = null
  private _inboxes: UiStoreTypes['InboxesUiStore'] | null = null
  private _scheduledMessages: UiStoreTypes['ScheduledMessagesUiStore'] | null = null
  private _sideMenu: UiStoreTypes['SideMenuUiStore'] | null = null
  private _phoneNumber: UiStoreTypes['PhoneNumberUiStore'] | null = null
  private _trustRegistration: UiStoreTypes['TrustRegistrationUiStore'] | null = null
  private _tollFreeRegistration: UiStoreTypes['TollFreeRegistrationUiStore'] | null = null
  private _workspace: UiStoreTypes['WorkspaceUiStore'] | null = null
  private _cnam: UiStoreTypes['CnamUiStore'] | null = null
  private _portRequest: UiStoreTypes['PortRequestUiStore'] | null = null
  private _helpAndSupport: UiStoreTypes['HelpAndSupportUiStore'] | null = null
  private _callsViewCache: UiStoreTypes['CallsViewUiStore'] | null = null
  private _aiCallAssistant: UiStoreTypes['AiCallAssistantUiStore'] | null = null
  private _aiKnowledge: UiStoreTypes['AiKnowledgeUiStore'] | null = null
  private _aiCustomJobsV1: UiStoreTypes['AiCustomJobsV1UiStore'] | null = null
  private _aiInboxes: UiStoreTypes['AiInboxesUiStore'] | null = null
  private scrollTimeout: number | null = null
  private storageWipeMarker: StorageWipeMarker | null = null

  intent: IntentStore
  /**
   * @deprecated Use `@ui/Toast/toasts` instead.
   */
  toast: ToastUiStore
  error: ErrorHelper
  sift?: Sift
  voice: VoiceUiStore
  readonly callPopOut: CallPopOutController
  prompt: PromptUiStore
  config = config
  confetti = false
  /** When true in dev, the LaunchDarkly dev toolbar is mounted (see global command palette). */
  launchDarklyToolbarVisible = false
  darkMode = true
  emojiPicker: EmojiPickerState = { open: false, targetRef: null }
  mediaViewer: MediaViewerState | null = null
  storesLoaded = false
  url: URLController
  readonly update: AppUpdateController
  notification: NotificationController
  themeKey: StorageThemeKey = 'system'
  themeVariant: ThemeVariant = 'default'
  typographyThemeKey: TypographyThemeKey = 'default'
  hasNetworkConnection = navigator.onLine
  isScrolling = false
  loadingPercentage = 0
  loadingStatus: LoadableStatus = 'idle'
  initializePromise: Promise<void>

  constructor(
    readonly electron: ElectronClient | null,
    readonly router: Router,
    readonly service: Service,
  ) {
    this.history = new HistoryManager(
      router,
      service.storage,
      this.isHistoryNavigationEnabled,
    )
    this.url = new URLController(this)
    this.notification = new NotificationController(this)
    this.onboarding = new OnboardingExperienceStore(this)
    this.command = new CommandUiStore(this)
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- UXP-5563 Remove legacy Toast API usage
    this.toast = new ToastUiStore()
    this.error = new ErrorHelper(this)
    this.sift = new Sift(this)
    this.update = new AppUpdateController(this)
    this.intent = new IntentStore(this.history.router)
    this.prompt = new PromptUiStore()
    this.voice = new VoiceUiStore(
      this.prompt,
      new UserMediaDeviceAccessUiStore(
        this.electron
          ? new UMDAElectronStrategy(this.electron)
          : new UMDAWebStrategy(permissions),
      ),
      this.service.voice,
    ).init()
    this.callPopOut = new CallPopOutController(this.service.presence)

    this.initializePromise = when(() => this.initialized && this.storesLoaded, {
      name: 'AppStore.initializePromise',
    })

    // The marker is written by the desktop main process when it wipes
    // storage (renderer crash, user reset). Report it as soon as the app is
    // back up — the user is logged out at this point, so waiting for
    // initialize() would miss anyone who never signs back in.
    void this.electron?.app
      .getStorageWipeMarker?.()
      .then((marker) => {
        if (!marker) {
          return
        }
        runInAction(() => {
          this.storageWipeMarker = marker
        })
        this.trackStorageWipe(marker, 'boot')
      })
      .catch(logError)

    makeAutoObservable(this, {
      config: false,
      debug: false,
      callPopOut: false,
      electron: false,
      history: false,
      onboarding: false,
      service: false,
      setThemeKey: action.bound,
      setTypographyThemeKey: action.bound,
      setThemeVariant: action.bound,
    })

    makePersistable(this, 'AppStore', {
      themeKey: this.service.storage.sync(),
      typographyThemeKey: this.service.storage.sync(),
      themeVariant: this.service.storage.sync(),
    })

    // Validate themeKey and themeVariant and reset to safe values if invalid
    runInAction(() => {
      const safeThemeKey = getSafeThemeKey(this.themeKey)
      if (safeThemeKey !== this.themeKey) {
        this.themeKey = safeThemeKey
      }

      const safeThemeVariant = getSafeThemeVariant(this.themeVariant)
      if (safeThemeVariant !== this.themeVariant) {
        this.themeVariant = safeThemeVariant
      }
    })

    // Sync initial theme to Amplitude Guides & Surveys when analytics becomes ready
    // This handles the case where themeKey is restored from storage via makePersistable
    OdysseyAnalyticsBridge.onReady(() => {
      OdysseyAnalyticsBridge.setGuidesAndSurveysThemeMode(
        toAmplitudeThemeMode(this.themeKey),
      )
    })

    when(
      () => service.authV2.isAuthenticated,
      () => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
        flowResult(this.initialize()).then(() =>
          this.service.transport.runPendingTransactions(),
        )
      },
    )

    when(
      () => this.update.initialUpdateStatus === 'complete',
      () => {
        try {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
          this.electron?.window.onInitialized?.()
        } catch {
          // ignore errors
        }
      },
    )

    // Once the app has rendered and flags loaded, warm the SW cache for offline
    // use, deferred to idle so it doesn't compete with the initial data load.
    when(
      () => this.update.initialUpdateStatus === 'complete' && service.flags.initialized,
      () => {
        if (!service.flags.flags.webAssetsPrecache) {
          return
        }

        const precache = () => this.update.serviceWorker?.precacheOfflineAssets()

        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(precache, { timeout: 10_000 })
        } else {
          window.setTimeout(precache, 2_000)
        }
      },
    )

    // TODO: UXP-4036 - Remove this since the reference to flags never change
    // and it can be done synchronously if needed and the rest of the logic can
    // move to domain stores
    reaction(
      () => [service.flags.flags, service.flags.initialized, this.initialized],
      () => {
        if (this.initialized && service.flags.initialized) {
          if (import.meta.env.PROD) {
            // assign a delay to avoid overloading the server
            const delay = exponentialDelay(60_000)
            // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
            delay().then(() => this.loadFlagDependentEssentials())
          } else {
            this.loadFlagDependentEssentials()
          }
        }
      },
      { name: 'AppStore.FlagsChanged' },
    )

    reaction(
      () =>
        this.initialized && service.organization.current?.id && service.transport.online,
      (shouldRun) => {
        if (shouldRun) {
          service.member.fetchPresence().catch(logError)
        }
      },
      { fireImmediately: true },
    )

    reaction(
      () => this.initialized,
      (loaded) => {
        if (loaded) {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
          this.loadUiStores()
        } else {
          this.tearDownUiStores()
        }
      },
      { fireImmediately: true },
    )

    reaction(
      () => this.unreadActivitiesCount,
      (count) => {
        this.electron?.app?.setBadgeCount?.(count).catch(() => null)
        navigator.setAppBadge?.(count).catch(() => null)
      },
      { fireImmediately: true },
    )

    this.service.voice.onRefreshRejected = (err: unknown) => {
      trackCustomAction({
        type: 'voice-token-refresh-error',
        error: err as Error,
      })

      // eslint-disable-next-line @typescript-eslint/no-deprecated -- UXP-5563 Remove legacy Toast API usage
      this.toast.showError('Could not refresh voice token.')

      if (err instanceof UpgradeRequiredError) {
        trackCustomAction({
          type: 'logging-out-user-upgrade-required',
        })

        this.service.clearAllAndRestart({ reason: 'upgrade-required' }).catch(logError)
        return
      }

      if (err instanceof Error) {
        trackCustomAction({
          type: 'logging-out-user-websocket-access-denied',
        })
      }
    }

    this.electron?.on('tray', (event) => {
      if (event.type === 'click') {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
        this.electron?.window.focus?.()
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
        this.electron?.window.show?.()
      }
    })

    if (this.is('windows')) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
      this.electron?.app.createTray?.()
    }

    this.setAsDefaultTelProtocol()

    document.addEventListener('scroll', this.onDocumentScroll, true)

    window.addEventListener(
      'online',
      action(() => {
        this.hasNetworkConnection = true
      }),
    )

    window.addEventListener(
      'offline',
      action(() => {
        this.hasNetworkConnection = false
      }),
    )

    // We need to handle Dexie error using addEventListener since `on('error')` is deprecated https://dexie.org/docs/Dexie/Dexie.on.error
    window.addEventListener('unhandledrejection', (error) => {
      if (
        error &&
        typeof error === 'object' &&
        error.reason &&
        typeof error.reason === 'object' &&
        'name' in error.reason
      ) {
        trackCustomAction({
          type: 'dexie-unhandled-rejection',
          error: new Error(`${error.reason}`),
        })

        const reason = error.reason as { name: string }
        if (reason.name === 'VersionError') {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
          this.signOut(undefined, 'dexie-version-error')
        }
      }
    })

    if (electron) {
      electron.on('open-status', () => {
        this.openStatus()
      })
      electron.on('open-whats-new', () => {
        this.openWhatsNew()
      })
      electron.on('open-resource-center', () => {
        this.openResourceCenter()
      })
      electron.on('open-chat', () => {
        this.sideMenu.handleHelpAndSupport()
      })
      electron.on('system-resume', () => {
        this.service.voice.startDevice()
      })
    }
  }

  get alerts(): UiStoreTypes['AlertsUiStore'] {
    if (this._alerts) {
      return this._alerts
    }
    throw new Error('alerts store is not initialized')
  }

  get billing(): UiStoreTypes['BillingUiStore'] {
    if (this._billing) {
      return this._billing
    }
    throw new Error('billing store is not initialized')
  }

  get callActivity(): UiStoreTypes['CallActivityUiStore'] {
    if (this._callActivity) {
      return this._callActivity
    }
    throw new Error('call activity store is not initialized')
  }

  get contacts(): UiStoreTypes['ContactsUiStore'] {
    if (this._contacts) {
      return this._contacts
    }
    throw new Error('contacts store is not initialized')
  }

  get conversation(): UiStoreTypes['ConversationUiStore'] {
    if (this._conversation) {
      return this._conversation
    }
    throw new Error('conversation store is not initialized')
  }

  get analytics(): UiStoreTypes['AnalyticsUiStore'] {
    if (this._analytics) {
      return this._analytics
    }
    throw new Error('analytics store is not initialized')
  }

  get inboxes(): UiStoreTypes['InboxesUiStore'] {
    if (this._inboxes) {
      return this._inboxes
    }
    throw new Error('inboxes store is not initialized')
  }

  get scheduledMessages(): UiStoreTypes['ScheduledMessagesUiStore'] {
    if (this._scheduledMessages) {
      return this._scheduledMessages
    }
    throw new Error('scheduled messages store is not initialized')
  }

  get sideMenu(): UiStoreTypes['SideMenuUiStore'] {
    if (this._sideMenu) {
      return this._sideMenu
    }
    throw new Error('side menu store is not initialized')
  }

  get phoneNumber(): UiStoreTypes['PhoneNumberUiStore'] {
    if (this._phoneNumber) {
      return this._phoneNumber
    }
    throw new Error('phone number store is not initialized')
  }

  get trustRegistration(): UiStoreTypes['TrustRegistrationUiStore'] {
    if (this._trustRegistration) {
      return this._trustRegistration
    }
    throw new Error('trust registration store is not initialized')
  }

  get tollFreeRegistration(): UiStoreTypes['TollFreeRegistrationUiStore'] {
    if (this._tollFreeRegistration) {
      return this._tollFreeRegistration
    }
    throw new Error('toll free registration store is not initialized')
  }

  get workspace(): UiStoreTypes['WorkspaceUiStore'] {
    if (this._workspace) {
      return this._workspace
    }
    throw new Error('workspace store is not initialized')
  }

  get cnam(): UiStoreTypes['CnamUiStore'] {
    if (this._cnam) {
      return this._cnam
    }
    throw new Error('cnam store is not initialized')
  }

  get portRequest(): UiStoreTypes['PortRequestUiStore'] {
    if (this._portRequest) {
      return this._portRequest
    }
    throw new Error('port request store is not initialized')
  }

  get helpAndSupport(): UiStoreTypes['HelpAndSupportUiStore'] | null {
    return this._helpAndSupport
  }

  get callsView(): UiStoreTypes['CallsViewUiStore'] {
    if (this._callsViewCache) {
      return this._callsViewCache
    }
    throw new Error('calls view store is not initialized')
  }

  get aiCallAssistant(): UiStoreTypes['AiCallAssistantUiStore'] {
    if (this._aiCallAssistant) {
      return this._aiCallAssistant
    }
    throw new Error('ai call assistant store is not initialized')
  }

  get aiKnowledge(): UiStoreTypes['AiKnowledgeUiStore'] {
    if (this._aiKnowledge) {
      return this._aiKnowledge
    }
    throw new Error('ai knowledge store is not initialized')
  }

  get aiCustomJobsV1(): UiStoreTypes['AiCustomJobsV1UiStore'] {
    if (this._aiCustomJobsV1) {
      return this._aiCustomJobsV1
    }
    throw new Error('ai custom jobs v1 store is not initialized')
  }

  get aiInboxes(): UiStoreTypes['AiInboxesUiStore'] {
    if (this._aiInboxes) {
      return this._aiInboxes
    }
    throw new Error('ai inboxes store is not initialized')
  }

  get isElectron() {
    return Boolean(this.electron)
  }

  get isStandaloneApp() {
    return this.isElectron || isPwa()
  }

  get isHistoryNavigationEnabled() {
    // Navigating through history is only supported on standalone apps
    // (i.e. the Desktop app or PWA) to make up for the missing native
    // browser functionality
    return this.isStandaloneApp
  }

  get isLoggedIn(): boolean {
    return this.service.authV2.isAuthenticated
  }

  get isFocused() {
    return document.hasFocus()
  }

  get needsOnboarding(): boolean | undefined {
    return this.onboarding.needsOnboarding
  }

  get onboardingExperience() {
    return this.onboarding.experience
  }

  get isSubscriptionReviewNull(): boolean {
    return (
      this.service.billing.subscription?.reviewStatus === null &&
      this.service.phoneNumber.loaded &&
      this.service.user.phoneNumbers.length > 0
    )
  }

  get isAccountFlagged(): boolean {
    return this.service.billing.subscription?.needsReview ?? false
  }

  get hasPendingInvites(): boolean {
    return this.service.user.invites.length > 0
  }

  private get onSocketsDown(): Observable<void> {
    return this.service.transport.connectivity.downtime.pipe(
      // Filter shorter downtimes
      filter((downtime) => downtime > 60_000),
      // Map to void
      mergeMap(() => of(undefined as void)),
      // Throttle so we don't spam the backend
      throttleTime(60_000, asyncScheduler, { leading: true, trailing: true }),
    )
  }

  onDataNeedsRefresh(
    ...args: [
      Parameters<typeof delayObservable>[1],
      Parameters<typeof delayObservable>[2]?,
    ]
  ) {
    return delayObservable(this.onSocketsDown, ...args)
  }

  get unreadActivitiesCount() {
    if (!this.storesLoaded) {
      return 0
    }

    let count = Object.entries(this.service.conversation.unreadCounts).reduce(
      (acc, [id, count]) => {
        if (this.inboxes.all.get(id)?.muted) {
          return acc
        }
        return acc + count
      },
      0,
    )
    count += this.alerts.unread
    count +=
      this.inboxes.dm?.conversations.list.reduce(
        (total, conversation) => total + conversation.unreadCount,
        0,
      ) ?? 0
    return count
  }

  get initialized(): boolean {
    return this.loadingStatus === 'success' && this.update.initialized
  }

  get initializationStatus(): LoadableStatus | 'updating' {
    return this.update.initialUpdateStatus === 'updating'
      ? 'updating'
      : this.loadingStatus
  }

  private get essentials(): (() => Promise<unknown>)[] {
    return [
      () => this.service.user.fetch(),
      () => this.service.user.fetchInvites(),
      () => this.service.phoneNumber.fetch(),
      () => this.service.member.fetch(),
      () => this.service.organization.fetch(),
      () => this.service.organization.fetchRoles(),
      () => this.service.organization.phoneNumber.fetch(),
      () => this.service.capabilities.fetch(),
      () => this.service.billing.fetchSubscription(),
      () => this.service.contact.deleteFromPublicApi(),
    ]
  }

  private get flagDependentEssentials(): (() => Promise<unknown>)[] {
    // Only admins and owners can fetch CNAM data.
    const shouldFetchCnam =
      this.service.capabilities.features.callerIdEnabled &&
      this.service.user.current?.asMember?.isAdmin
    const snippetsEnabled = this.service.capabilities.features.snippetsEnabled

    const shouldFetchTrustRegistration = this.service.flags.flags.a2pBlockMessaging

    return [
      shouldFetchCnam ? () => this.service.cnam.fetch() : null,
      snippetsEnabled ? () => this.service.snippet.fetch() : null,
      shouldFetchTrustRegistration
        ? () => this.service.trustRegistrationV2.fetch()
        : null,
      () => this.service.tollFreeRegistration.fetchLite(),
    ].filter(isNonNull)
  }

  private get loadingPercentageStep(): number {
    return Math.round(100 / this.essentials.length)
  }

  private onDocumentScroll = action(() => {
    if (this.scrollTimeout) {
      window.clearTimeout(this.scrollTimeout)
    }
    this.isScrolling = true
    this.scrollTimeout = window.setTimeout(
      action(() => {
        this.isScrolling = false
      }),
      100,
    )
  })

  is(platform: 'web' | 'mac' | 'windows') {
    const current = ((): 'web' | 'mac' | 'windows' => {
      switch (this.electron?.platform) {
        case 'darwin':
          return 'mac'
        case 'win32':
          return 'windows'
        default:
          return 'web'
      }
    })()
    return current === platform
  }

  setThemeKey(themeKey: StorageThemeKey) {
    this.themeKey = themeKey

    // Sync theme to Amplitude Guides & Surveys
    // Use onReady to handle case where analytics isn't initialized yet (e.g., initial load from storage)
    const amplitudeThemeMode = toAmplitudeThemeMode(themeKey)
    OdysseyAnalyticsBridge.onReady(() => {
      OdysseyAnalyticsBridge.setGuidesAndSurveysThemeMode(amplitudeThemeMode)
    })
  }

  setThemeVariant(themeVariant: ThemeVariant) {
    this.themeVariant = themeVariant
  }

  setTypographyThemeKey(typographyThemeKey: TypographyThemeKey) {
    this.typographyThemeKey = typographyThemeKey
  }

  showEmojiPicker = (props: Omit<EmojiPickerPopoverProps, 'children'>) => {
    this.emojiPicker = { open: true, ...props }
  }

  hideEmojiPicker = () => {
    this.emojiPicker = { open: false, targetRef: null }
  }

  openMediaViewer = (media: MessageMediaModel[], index: number) => {
    this.mediaViewer = { media, index }
  }

  closeMediaViewer = () => {
    this.mediaViewer = null
  }

  reset = async () => {
    this.prompt.tearDown()
    this.emojiPicker = { open: false, targetRef: null }

    if (this.update.serviceWorker) {
      await this.update.serviceWorker.unregister()
    }

    await this.service.reset()
  }

  signOut = async (envOverride?: Env, reason: LogoutReason = 'manual') => {
    this.tearDown()

    if (this.update.serviceWorker) {
      await this.update.serviceWorker.unregister()
    }

    if (reason === 'manual') {
      trackCustomAction({
        type: 'universal-login-manual-logout',
      })
    }

    await this.service.clearAllAndRestart({ reason, envOverride })
  }

  showConfetti = () => {
    this.confetti = true
  }

  resetConfetti = () => {
    this.confetti = false
  }

  toggleLaunchDarklyToolbar = () => {
    this.launchDarklyToolbarVisible = true
    window?.ldToolbar?.toggle?.()
  }

  openResourceCenter = () => {
    const url = new URL('https://support.openphone.com/')
    this.service.analytics.click('Help center', url)

    window.open(url, '_blank')
  }

  openWhatsNew = () => {
    const url = new URL('https://support.openphone.com/changelog/')
    this.service.analytics.click("What's new", url)
    window.open(url, '_blank')
  }

  openStatus = () => {
    const url = new URL('https://status.openphone.co/')
    this.service.analytics.click('Status', url)
    window.open(url, '_blank')
  }

  focus() {
    if (this.electron) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
      this.electron.window.show?.()
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
      this.electron.window.focus?.()
    } else {
      window.focus()
    }
  }

  incrementLoadingPercentage() {
    this.loadingPercentage += this.loadingPercentageStep
  }

  *initialize() {
    try {
      this.loadingStatus = 'loading'

      yield this.loadEssentials()

      yield this.postInitialize()

      // there are reactions that depend on this flag to execute
      // these reactions instantiate stores that assume feature
      // flags are properly loaded and evaluated
      //
      // therefore we must wait for `postInitialize` before
      // marking the loading process complete
      this.loadingStatus = 'success'
    } catch (error) {
      logError(error)
      this.loadingStatus = 'failed'
      if (error instanceof ForbiddenError) {
        trackCustomAction({
          type: 'logging-out-user-forbidden-error',
        })

        this.signOut(undefined, 'forbidden').catch(logError)
      }
    }
  }

  private trackStorageWipe(marker: StorageWipeMarker, phase: 'boot' | 'post-login') {
    trackCustomAction({
      type: 'desktop-storage-wiped',
      phase,
      reason: marker.reason,
      msSinceWipe: Date.now() - marker.at,
      crashReason: marker.crashReason ?? null,
      exitCode: marker.exitCode ?? null,
      userId: marker.userId ?? null,
      orgId: marker.organizationId ?? null,
    })
  }

  async postInitialize() {
    void this.loadNotSoEssentials()

    // enable chat bot menu option in the electron app
    void this.electron?.app.enableChatBot?.()

    // Second half of the wipe funnel: the 'boot' event fired in the
    // constructor; this one confirms the wiped user made it back in.
    if (this.storageWipeMarker) {
      this.trackStorageWipe(this.storageWipeMarker, 'post-login')
      runInAction(() => {
        this.storageWipeMarker = null
      })
    }

    const user = this.service.user.getCurrentUser()
    const organization = this.service.organization.getCurrentOrganization()
    const subscription = this.service.billing.getCurrentSubscription()
    const member = user.asMember
    if (!member) {
      throw new Error('Member for current user could not be loaded')
    }

    if (
      member.email?.endsWith('openphone.co') ||
      member.email?.endsWith('openphone.com') ||
      member.email?.endsWith('@quo.com') ||
      this.service.authV2.isImpersonating
    ) {
      window.app = this
    }

    // it's important to wait for identification because this
    // waits for launchdarkly to properly evaluate flag rules
    // and any logic that depends on feature flags after this
    // won't have the correct flag value
    await this.identify({
      user,
      member,
      organization,
      subscription,
      workspaceSize: this.service.member.collection.length,
      desktopVersion: this.service.desktopVersion,
      swVersion: this.update.serviceWorker?.swVersion ?? null,
    })
    this.log.debug('Identified user with ID {userId}')

    trackCustomAction({
      type: 'universal-login-init-websocket',
    })
    await this.service.transport.websocket.connect()
    this.identifyDeferredProperties()
  }

  private *loadEssentials() {
    const promises: Promise<unknown>[] = []

    for (const essential of this.essentials) {
      promises.push(essential().then(() => this.incrementLoadingPercentage()))
    }

    yield Promise.all(promises)
  }

  async loadNotSoEssentials() {
    await Promise.all([
      this.service.organization.fetchAiAgentSystemUser(),
      this.service.contact.loadCsvImportsV2(),
      this.service.workspace.fetchUserGroups(),
      this.service.blocklist.fetch(),
      this.service.integration.fetchAll(),
      this.service.portRequest.fetch(),
      this.service.contact.fetchSettings(),
      this.service.ringOrder.fetchMaxDialAtOnce(),
      this.service.phoneNumber.getBlockedCountryCodes(),
    ])

    // Pre-warm the memberIdsWithPhones cache so dialer opens instantly
    // This triggers the computed to evaluate once, then MobX caches it
    void this.service.member.memberIdsWithPhones

    // Pre-warm the phone number selector sort worker so it's ready when dialer opens
    if (this.service.flags.flags.webPhoneNumberSelectorSortWorker) {
      void import('@src/lib/workers/PhoneNumberSelectorSortManager').then(
        ({ getSharedSortManager }) => getSharedSortManager(),
      )
    }
  }

  /**
   * Like loadEssentials, but for services that depend on a flag being enabled.
   *
   * Should get called after the user has been properly identified.
   */
  private *loadFlagDependentEssentials() {
    const promises: Promise<unknown>[] = []

    for (const flagDependentEssential of this.flagDependentEssentials) {
      promises.push(
        flagDependentEssential().then(() => this.incrementLoadingPercentage()),
      )
    }

    yield Promise.all(promises)
  }

  private async identify({
    user,
    member,
    organization,
    subscription,
    workspaceSize,
    desktopVersion,
    swVersion,
  }: IdentifyData & { swVersion: string | null; desktopVersion?: string }) {
    setRootLogger(
      getRootLogger().with({
        userId: user.id,
        organizationId: organization.id,
        swVersion,
        desktopVersion,
      }),
    )

    // Set user for observability
    if (member?.id) {
      setUser({
        'user.id': member.id,
        'user.email': member.email ?? '',
        'user.name': member.name ?? '',
        'user.role': member.role ?? '',
        'org.id': organization.id,
        'org.plan': subscription.type ?? undefined,
      })
    }

    // Identify the user in the Feature Flags service
    await this.service.flags.identify(
      user,
      organization,
      subscription,
      workspaceSize,
      desktopVersion,
    )

    // Identify the user in the survey service
    void this.service.survey.identify(user)

    // Identify the user in Ketch (consent management)
    setKetchIdentities({
      userId: user.id,
      email: user.email,
      phoneNumber: user.verifiedPhoneNumber,
    })

    // Identify user in the Electron app
    await this.electron?.app
      .identify?.({
        userId: user.id,
        userEmail: user.email ?? '',
        userRole: member.role,
        userName: member.name,
        organizationId: organization.id,
        appVersion: config.VERSION,
        workspaceSize: workspaceSize,
        authFlowType: 'universal-login',
      })
      .catch(logError)

    // Identify the user in the analytics
    try {
      await this.service.analytics.identify({
        user,
        member,
        organization,
        subscription,
        workspaceSize,
      })
    } catch (error) {
      logError(error)
    }

    // Identify the user in Odyssey Analytics
    try {
      if (OdysseyAnalyticsBridge.isInitialized()) {
        const experimentFromReferral = isExperimentFromReferral()
        await OdysseyAnalyticsBridge.identify(
          user.id,
          {
            app_version: config.VERSION,
            auth_flow_type: 'universal-login',
            default_inbox_id: this._inboxes?.defaultSelected?.id ?? '',
            default_inbox_name:
              this._inboxes?.defaultSelected?.phoneNumber?.formattedName ?? '',
            desktop_version: this.service.desktopVersion,
            onboarding_industry: organization.analytics?.industry,
            onboarding_need: organization.analytics?.need,
            onboarding_number_intent: organization.analytics?.numberIntent,
            onboarding_size: organization.analytics?.teamSize,
            onboarding_teams: organization.analytics?.teams,
            onboarding_usage_intent: organization.analytics?.usageIntent,
            onboarding_use_case: organization.analytics?.useCase,
            onboarding_users: organization.analytics?.users,
            org_creation_date: organization.createdAt
              ? formatDateToISO(organization.createdAt)
              : '',
            service_worker_version: swVersion ?? 'UNKNOWN VERSION',
            signup_date: user.createdAt ? formatDateToISO(user.createdAt) : '',
            subscription_end_date: subscription.currentPeriodExpiresAt
              ? formatDateToISO(subscription.currentPeriodExpiresAt)
              : '',
            subscription_start_date: subscription.originalStartedAt
              ? formatDateToISO(subscription.originalStartedAt)
              : '',
            billing_platform: subscription.store ?? '',
            subscription_interval: subscription.annual ? 'annual' : 'monthly',
            org_subscription_status: subscription.trial
              ? 'trial'
              : subscription.isActive
                ? 'active'
                : 'inactive',
            org_inboxes: this.service.phoneNumber.collection.list.length,
          },
          {
            ...(experimentFromReferral ? { has_coupon_or_invite: true } : {}),
          },
        )
      }
    } catch (error) {
      logError(error)
    }
  }

  /**
   * Updates Amplitude with the default inbox properties once inboxes are loaded.
   * This is called after loadUiStores to ensure the default inbox values are populated.
   * Uses onReady to handle the race condition where the analytics bridge may not
   * be initialized yet when this method is called.
   */
  private updateDefaultInboxInAnalytics() {
    const defaultInbox = this._inboxes?.defaultSelected
    if (!defaultInbox) {
      return
    }

    const user = this.service.user.current
    if (!user) {
      return
    }

    // Use onReady to ensure the identify call happens even if the analytics
    // bridge isn't initialized yet
    OdysseyAnalyticsBridge.onReady(() => {
      OdysseyAnalyticsBridge.identify(user.id, {
        default_inbox_id: defaultInbox.id ?? '',
        default_inbox_name: defaultInbox.phoneNumber?.formattedName ?? '',
      }).catch(logError)
    })
  }

  /**
   * Sets up deferred analytics identify calls for data that isn't available
   * at initial identify time (loaded asynchronously after essentials).
   *
   * Each provider exposes a computed `analyticsProperties` getter that returns
   * `undefined` while data is loading and a properties object when ready.
   */
  private identifyDeferredProperties() {
    const user = this.service.user.current
    if (!user) {
      return
    }

    const providers: { analyticsProperties: Record<string, unknown> | undefined }[] = [
      this.service.integration,
      this.service.billing.addon,
      this.service.trustRegistrationV2,
    ]

    for (const provider of providers) {
      // Fires once when the provider's analyticsProperties transitions from
      // undefined (loading) to a defined object, then self-disposes.
      when(
        () => provider.analyticsProperties !== undefined,
        () => {
          OdysseyAnalyticsBridge.identify(user.id, provider.analyticsProperties).catch(
            logError,
          )
        },
      )
    }
  }

  private setAsDefaultTelProtocol() {
    const hasBeenSetAlready = localStorage.getItem(HAS_BEEN_SET_AS_DEFAULT_TEL_PROTOCOL)
    if (this.electron && !hasBeenSetAlready) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
      this.electron.app.registerTelProtocol?.()
      localStorage.setItem(HAS_BEEN_SET_AS_DEFAULT_TEL_PROTOCOL, '1')
    }
  }

  private async loadUiStores() {
    const {
      AlertsUiStore,
      BillingUiStore,
      CallActivityUiStore,
      ContactsUiStore,
      ConversationUiStore,
      HelpAndSupportUiStore,
      InboxesUiStore,
      SideMenuUiStore,
      PhoneNumberUiStore,
      ScheduledMessagesUiStore,
      AnalyticsUiStore,
      WorkspaceUiStore,
      CnamUiStore,
      PortRequestUiStore,
      TollFreeRegistrationUiStore,
      TrustRegistrationUiStore,
      CallsViewUiStore,
      AiCallAssistantUiStore,
      AiKnowledgeUiStore,
      AiCustomJobsV1UiStore,
      AiInboxesUiStore,
    } = await loadUiStores()

    runInAction(() => {
      this._alerts = new AlertsUiStore(this)
      this._billing = new BillingUiStore(this)
      this._contacts = new ContactsUiStore(this)
      this._inboxes = new InboxesUiStore(this)
      this._sideMenu = new SideMenuUiStore(this)
      this._phoneNumber = new PhoneNumberUiStore(this)
      this._callActivity = new CallActivityUiStore(
        this.service.flags,
        this.service.user,
        this.service.member,
        this._phoneNumber,
        this.service.organization,
      )
      this._conversation = new ConversationUiStore(this, this._callActivity)
      this._scheduledMessages = new ScheduledMessagesUiStore(this)
      this._analytics = new AnalyticsUiStore(this)
      this._workspace = new WorkspaceUiStore(this)
      this._cnam = new CnamUiStore(this)
      this._portRequest = new PortRequestUiStore(this)
      this._tollFreeRegistration = new TollFreeRegistrationUiStore(this)
      this._trustRegistration = new TrustRegistrationUiStore(this)
      this._helpAndSupport = new HelpAndSupportUiStore(this)

      this._callsViewCache = new CallsViewUiStore(
        this.service.activity,
        this.service.transport,
        {
          itemsPerPage: 50,
        },
      )
      this._aiCallAssistant = new AiCallAssistantUiStore(
        this.service.ai,
        this.service.transport,
      )
      this._aiKnowledge = new AiKnowledgeUiStore(
        this.service.ai,
        this.service.transport,
        this.service.analytics,
      )
      this._aiCustomJobsV1 = new AiCustomJobsV1UiStore(
        this.service.ai,
        this.service.transport,
        this.service.analytics,
        this.service.flags,
        this.service.integration,
      )
      this._aiInboxes = new AiInboxesUiStore(
        this.service.workflow,
        this.service.organization,
        this.service.user,
        this.inboxes,
        this.service.capabilities,
      )
      this.storesLoaded = true
      this.url.checkHandlerUrl()

      // Update Amplitude when default inbox changes (similar to CommandAI's reaction)
      // This handles the case where defaultSelected is computed after inboxes load
      reaction(
        () => this._inboxes?.defaultSelected,
        () => this.updateDefaultInboxInAnalytics(),
        { fireImmediately: true },
      )
    })
  }

  private tearDownUiStores() {
    this._callActivity?.tearDown()
    this._callActivity = null
    this._alerts?.tearDown()
    this._alerts = null
    this._billing?.tearDown()
    this._billing = null
    this._contacts?.tearDown()
    this._contacts = null
    this._conversation?.tearDown()
    this._conversation = null
    this._inboxes?.tearDown()
    this._inboxes = null
    this._sideMenu?.tearDown()
    this._sideMenu = null
    this._phoneNumber?.tearDown()
    this._phoneNumber = null
    this._scheduledMessages?.tearDown()
    this._scheduledMessages = null
    this._analytics?.tearDown()
    this._analytics = null
    this._cnam?.tearDown()
    this._cnam = null
    this._portRequest?.tearDown()
    this._portRequest = null
    this._tollFreeRegistration?.tearDown()
    this._tollFreeRegistration = null
    this._trustRegistration?.tearDown()
    this._trustRegistration = null
    this._workspace?.tearDown()
    this._workspace = null
    this._helpAndSupport?.tearDown()
    this._helpAndSupport = null
    this.storesLoaded = false
  }

  tearDown = () => {
    this.prompt.tearDown()
    this.voice.tearDown()
    this.callPopOut.tearDown()
    this.emojiPicker = { open: false, targetRef: null }
    document.removeEventListener('scroll', this.onDocumentScroll, true)
    this._helpAndSupport?.tearDown()
  }
}

/**
 * The max number of recently viewed history items to keep in browser storage.
 *
 * This is larger than the number of items we show in the UI to add buffer
 * in case some the stored locations result in invalid or deleted data.
 */
export const MAX_STORED_RECENTLY_VIEWED_HISTORY_ITEMS =
  MAX_VISIBLE_RECENTLY_VIEWED_HISTORY_ITEMS + 12

/**
 * The max number of location history items to keep in browser storage.
 */
export const MAX_STORED_LOCATION_HISTORY_ITEMS = 40

export class HistoryManager {
  location: Location
  /**
   * A list of curated locations that the user has recently viewed
   * and will be displayed in the Recently Viewed list.
   */
  recentlyViewedLocations: Location[] = []
  historyNavigate: SafeAbsoluteNavigateFunction

  /**
   * A list of all location history entries tracked for the purpose of
   * detecting when the forward and back buttons should be enabled or
   * disabled in the Desktop app and PWA.
   */
  locationHistory: Location[] = []
  currentHistoryIndex: number = -1

  constructor(
    readonly router: Router,
    storage: StorageService,
    readonly isHistoryNavigationEnabled: boolean,
  ) {
    this.location = router.state.location
    this.historyNavigate = createSafeAbsoluteNavigate(router)
    this.initializeLocationHistory()

    makeAutoObservable(this, {})

    makePersistable(this, 'HistoryManager', {
      recentlyViewedLocations: storage.async(),
      locationHistory: storage.async(),
      currentHistoryIndex: storage.async(),
    })

    router.subscribe(
      action((state) => {
        this.updateRecentlyViewedLocations(state)
        this.updateLocationHistory(state)
        this.location = state.location
      }),
    )
  }

  private updateRecentlyViewedLocations(state: Router['state']) {
    // We only care about tracking history if the feature to navigate
    // through it is enabled
    if (!this.isHistoryNavigationEnabled) {
      return
    }

    // Strip the search, hash, and state for now to keep the recently viewed list
    // simple and not have to worry about displaying additional information in
    // the UI regarding it.
    const newRecentlyViewedLocation = {
      ...state.location,
      search: '',
      hash: '',
      state: null,
    }

    // Only handle PUSH and REPLACE actions and not POP. The focus is getting
    // items into the list, not trying to be smart about removing them.
    if (
      state.historyAction === NavigationType.Push ||
      state.historyAction === NavigationType.Replace
    ) {
      // Remove potential duplicates before adding the new location to the end.
      //
      // This only needs to compare pathnames since we're stripping the search,
      // hash, and state (see the comment two above). If we decide to support
      // locations with different searches, hashes, or states, we'll need to
      // update this to use our `router/isSameLocation` util.
      const dedupedRecentlyViewedLocations = this.recentlyViewedLocations.filter(
        (location) => location.pathname !== newRecentlyViewedLocation.pathname,
      )

      // Limit the number of items kept in local storage so it doesn't grow indefinitely
      this.recentlyViewedLocations = [
        ...dedupedRecentlyViewedLocations,
        newRecentlyViewedLocation,
      ].slice(-MAX_STORED_RECENTLY_VIEWED_HISTORY_ITEMS)
    }
  }

  private initializeLocationHistory() {
    // We only care about initializing location history if the feature to navigate
    // through it is enabled
    if (!this.isHistoryNavigationEnabled) {
      return
    }

    this.locationHistory = [this.location]
    this.currentHistoryIndex = 0
  }

  /**
   * Ensures the location history does not exceed the maximum allowed size.
   */
  private limitLocationHistorySize() {
    if (this.locationHistory.length > MAX_STORED_LOCATION_HISTORY_ITEMS) {
      this.locationHistory = this.locationHistory.slice(
        -MAX_STORED_LOCATION_HISTORY_ITEMS,
      )
    }
  }

  /**
   * Handles a navigation push event, meaning a new location is being added.
   *
   * Removes any entries after the current index (clearing the forward history),
   * adds the new location, and ensures the current index points to it.
   */
  private onNavigationPush(state: Router['state']) {
    const newLocation = state.location

    this.locationHistory = [
      ...this.locationHistory.slice(0, this.currentHistoryIndex + 1),
      newLocation,
    ]

    this.limitLocationHistorySize()
    this.currentHistoryIndex = this.locationHistory.length - 1
  }

  /**
   * Handles a navigation replace event, meaning the current location is being
   * updated with no index change.
   *
   * If history exists, updates the current entry to the new location.
   * If history is empty, initializes the history with the new location.
   */
  private onNavigationReplace(state: Router['state']) {
    const newLocation = state.location
    if (this.locationHistory.length > 0) {
      this.locationHistory[this.currentHistoryIndex] = newLocation
    } else {
      this.locationHistory = [newLocation]
      this.currentHistoryIndex = 0
    }
  }

  /**
   * Handles a navigation pop event, meaning the index was changed.
   *
   * Attempts to find the closest matching location in the history and sets the current
   * history index to it. If no match is found, adds the new location as a new entry.
   */
  private onNavigationPop(state: Router['state']) {
    const newLocation = state.location
    let matchingIndex = -1
    let minDistance = Infinity

    for (let i = 0; i < this.locationHistory.length; i++) {
      const historyLocation = this.locationHistory[i]

      // this shouldn't be undefined but guard to make TS happy
      if (!historyLocation) {
        continue
      }

      // stop if the location keys match since it means we found an exact match
      if (historyLocation.key === newLocation.key) {
        matchingIndex = i
        break
      }

      if (isSameLocation(historyLocation, newLocation)) {
        const distance = Math.abs(i - this.currentHistoryIndex)
        if (distance > 0 && distance < minDistance) {
          minDistance = distance
          matchingIndex = i
        }
      }
    }

    if (matchingIndex !== -1) {
      this.currentHistoryIndex = matchingIndex
    } else {
      this.locationHistory.push(newLocation)
      this.limitLocationHistorySize()
      this.currentHistoryIndex = this.locationHistory.length - 1
    }
  }

  private updateLocationHistory(state: Router['state']) {
    // We only care about tracking location history if the feature to navigate
    // through it is enabled
    if (!this.isHistoryNavigationEnabled) {
      return
    }

    switch (state.historyAction) {
      case NavigationType.Push: {
        this.onNavigationPush(state)
        break
      }
      case NavigationType.Replace: {
        this.onNavigationReplace(state)
        break
      }
      case NavigationType.Pop: {
        this.onNavigationPop(state)
        break
      }
      default: {
        assertNever(
          state.historyAction,
          `Unhandled history action: ${state.historyAction}`,
        )
      }
    }
  }

  /**
   * Whether the user can navigate backwards in location history
   */
  get canGoBack(): boolean {
    if (!this.isHistoryNavigationEnabled || this.locationHistory.length === 0) {
      return false
    }

    return this.currentHistoryIndex > 0
  }

  /**
   * Whether the user can navigate forwards in location history
   */
  get canGoForward(): boolean {
    if (!this.isHistoryNavigationEnabled || this.locationHistory.length === 0) {
      return false
    }

    return this.currentHistoryIndex < this.locationHistory.length - 1
  }

  get pathComponents() {
    return this.location.pathname.split('/').slice(1)
  }

  /**
   * Returns everything after the ? in the url as a hash, so ?a=1&b2
   * turns into { a: 1, b: 2 }
   */
  get query(): LocationSearch {
    return fromQueryString(this.location.search)
  }

  push = (path: string, search?: URLSearchParams | string) => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
    this.router.navigate({
      pathname: path,
      search: stripUnknownParams(search ?? this.location.search).toString(),
    })
  }

  replace = (path: string, search?: URLSearchParams | string) => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
    this.router.navigate(
      {
        pathname: path,
        search: stripUnknownParams(search ?? this.location.search).toString(),
      },
      { replace: true },
    )
  }

  consumeQueryParam = (param: LocationSearchParam): string | undefined => {
    const value = this.query[param]

    if (value) {
      const search = stripSearchParam(this.location.search, param)

      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
      this.router.navigate(
        {
          ...this.location,
          search: search.toString(),
        },
        { replace: true },
      )
    }

    return value
  }

  goBack = () => {
    void this.router.navigate(-1)
  }

  goForward = () => {
    void this.router.navigate(1)
  }
}
